/**
 * /litters/upcoming/:id — Breeder's manage page for a single upcoming
 * litter listing. Lets them publish (pay listing fee), upload + sign
 * the contract, see paid deposits / waitlist, feature-boost, and mark
 * the litter as whelped (converts into a real /litters row).
 */
import React, { useEffect, useState, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateOfBirthInput from "@/components/DateOfBirthInput";
import { formatApiError } from "@/lib/utils-app";
import { useUserPlanTier } from "@/hooks/useUserPlanTier";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";

const STAGE_LABEL = {
  draft: "Draft (not published)",
  planned: "Planned breeding · live",
  expecting: "Expecting · live",
  whelped: "Whelped · litter created",
  converted: "Closed · archived",
};

const DOLLARS = (cents) =>
  cents == null ? "—" : `$${(cents / 100).toFixed(0)}`;

export default function UpcomingLitterDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [contract, setContract] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [legalName, setLegalName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [whelpDate, setWhelpDate] = useState("");
  const [puppies, setPuppies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  // Plan tier drives both the publish-button label ("Free with Kennel
  // Elite" vs "$25") and how we handle the publish response (Elite
  // skips Stripe; the old code blindly did window.location.assign(null)
  // on the null checkout_url, which is what looked like "trying to
  // charge me" on mobile — a phantom navigation to /null).
  const { tier: planTier, isProOrElite } = useUserPlanTier();
  // Promo codes apply to the per-publish basic-tier fee. Pro/Elite
  // already get free publishes so the promo input is suppressed for
  // them — no need to clutter the UI with a discount field on a $0
  // checkout.
  const [promo, setPromo] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/upcoming-breedings/${id}`);
      setDoc(r.data);
      try {
        const c = await api.get(`/upcoming-breedings/${id}/contract`);
        setContract(c.data || null);
      } catch {
        setContract(null);
      }
      try {
        const d = await api.get(`/upcoming-breedings/${id}/deposits`);
        setDeposits(d.data || []);
      } catch {
        setDeposits([]);
      }
      try {
        const p = await api.get(`/upcoming-breedings/${id}/puppies`);
        setPuppies(p.data || []);
      } catch {
        setPuppies([]);
      }
      try {
        const a = await api.get(`/upcoming-breedings/${id}/analytics`);
        setAnalytics(a.data || null);
      } catch {
        setAnalytics(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle returns from Stripe.
  useEffect(() => {
    const session = params.get("session_id");
    if (!session) return;
    if (params.get("publish") === "ok") {
      api.get(`/upcoming-breedings/${id}/checkout-status/${session}`).then(() => {
        toast.success("Listing published.");
        load();
      });
    } else if (params.get("feature") === "ok") {
      api.get(`/upcoming-breedings/${id}/feature-status/${session}`).then(() => {
        toast.success("Featured boost activated.");
        load();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, id]);

  const publish = async (target_stage) => {
    if (!ack) return toast.error("Acknowledge the venue disclaimer.");
    if (!legalName.trim()) return toast.error("Enter your full legal name.");
    setBusy(true);
    try {
      const r = await api.post(`/upcoming-breedings/${id}/publish`, {
        target_stage,
        venue_disclaimer_acknowledged: true,
        legal_name: legalName.trim(),
        origin_url: webOrigin(),
        promo_code: promo?.valid ? promo.code : undefined,
      });
      // Pro / Kennel Elite tiers get a free publish — backend returns
      // {free_publish: true, checkout_url: null}. We just refresh the
      // page state instead of redirecting to /null (the previous
      // behavior, which looked like an attempted charge to the user).
      if (r.data?.free_publish || !r.data?.checkout_url) {
        toast.success(
          r.data?.tier === "kennel_elite"
            ? "Published — free with Kennel Elite."
            : r.data?.tier === "pro"
              ? "Published — free with Breeder Pro."
              : "Published.",
        );
        await load();
        return;
      }
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const featureBoost = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/upcoming-breedings/${id}/feature`, {
        origin_url: webOrigin(),
      });
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadContract = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/upcoming-breedings/${id}/contract/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Contract uploaded. Sign it to publish to buyers.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const signContract = async () => {
    if (!ack || !legalName.trim()) {
      return toast.error("Legal name and venue acknowledgement required.");
    }
    setBusy(true);
    try {
      await api.post(`/upcoming-breedings/${id}/contract/sign-breeder`, {
        legal_name: legalName.trim(),
        venue_disclaimer_acknowledged: true,
      });
      toast.success("Contract published. Buyers can now sign.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markWhelped = async () => {
    if (!whelpDate) return toast.error("Pick the actual whelp date.");
    setBusy(true);
    try {
      const r = await api.post(`/upcoming-breedings/${id}/whelp`, {
        target_stage: "whelped",
        whelp_date: whelpDate,
      });
      toast.success("Whelped. A new litter has been created.");
      nav(`/litters/${r.data.litter_id}/confirmation`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const updatePuppy = async (dogId, patch) => {
    try {
      await api.post(`/upcoming-breedings/${id}/puppies/${dogId}/assign`, patch);
      toast.success("Puppy saved.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!doc) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-[#6B6B6B]">Loading…</div>
      </AppShell>
    );
  }

  const stage = doc.stage || "draft";
  const isPublished = !!doc.listing_fee_paid_at;
  const isFeatured =
    doc.featured_until && new Date(doc.featured_until) > new Date();

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
              {STAGE_LABEL[stage] || stage}
            </p>
            <h1 className="font-spectral text-3xl tracking-tight">
              {doc.dam_snapshot?.name} × {doc.sire_snapshot?.name}
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {doc.breed} · {DOLLARS(doc.price_per_puppy_cents)} per pup · deposit {DOLLARS(doc.deposit_amount_cents)}
            </p>
          </div>
          {isPublished && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const cardUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/upcoming-breedings/${id}/share-card.png`;
                  // OG-rich share URL: when pasted to FB/IG/X/iMessage,
                  // crawlers scrape this and see the 1200×630 preview.
                  // Humans get a 0s redirect to the SPA listing.
                  const shareUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/upcoming-breedings/${id}/share`;
                  window.open(cardUrl, "_blank", "noopener");
                  navigator.clipboard
                    ?.writeText(shareUrl)
                    .then(() =>
                      toast.success(
                        "Public link copied + share image opened in a new tab."
                      )
                    )
                    .catch(() => {});
                }}
                className="text-xs underline text-ink"
                data-testid="upcoming-share-card-btn"
              >
                Share card →
              </button>
              <Link
                to={`/marketplace/upcoming/${id}`}
                className="text-xs underline text-ink"
                data-testid="upcoming-view-public"
              >
                View public listing →
              </Link>
            </div>
          )}
        </div>

        {/* Analytics — render only once the listing is published.
            Drafts have no data worth showing. */}
        {isPublished && analytics && (
          <AnalyticsStrip a={analytics} />
        )}

        {/* Publish gate */}
        {!isPublished && (
          <section className="rounded-xl border border-[#E5D08A] bg-[#FBF7EC] p-5">
            <h2 className="font-spectral text-xl tracking-tight">Publish</h2>
            <p className="text-sm text-[#3F3F3F] mt-1">
              {isProOrElite ? (
                <>
                  Listing fees are <strong>free</strong> on your{" "}
                  {planTier === "kennel_elite" ? "Kennel Elite" : "Breeder Pro"} plan.
                  Pick the stage that matches reality and you&rsquo;re live.
                </>
              ) : (
                <>
                  Pay the one-time listing fee to make this visible on the
                  marketplace. Stage: planned ($25) or expecting ($40).
                </>
              )}
            </p>
            <div className="mt-3 space-y-3">
              <Input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Your full legal name"
                data-testid="upcoming-publish-legalname"
              />
              <label className="flex items-start gap-2 text-[12px] text-[#3F3F3F]">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  className="mt-0.5"
                  data-testid="upcoming-publish-ack"
                />
                <span>
                  I acknowledge that <b>Armstrong K9 Registry is a venue</b>, not a party to any
                  contract. Armstrong is not the breeder, buyer, guarantor, or legal
                  counsel. Money flows directly to me via Stripe Connect. I am the
                  merchant of record for buyer deposits.
                </span>
              </label>
              {!isProOrElite && (
                <>
                <CompBanner className="mb-3" />
                <PromoCodeInput
                  kind="upcoming_breeding_publish"
                  amount={25}
                  applied={promo}
                  onApplied={(p) => {
                    setPromo(p);
                    toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
                  }}
                  onCleared={() => setPromo(null)}
                />
                </>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  disabled={busy}
                  onClick={() => publish("planned")}
                  className="bg-ink hover:bg-[#0a0a0a] text-white rounded-full w-full sm:flex-1 h-11"
                  data-testid="upcoming-publish-planned"
                >
                  {isProOrElite ? "Publish as Planned — Free" : "Publish as Planned — $25"}
                </Button>
                <Button
                  disabled={busy}
                  onClick={() => publish("expecting")}
                  className="bg-[#C1272D] hover:bg-[#a3211f] text-white rounded-full w-full sm:flex-1 h-11 border-0"
                  data-testid="upcoming-publish-expecting"
                >
                  {isProOrElite ? "Publish as Expecting — Free" : "Publish as Expecting — $40"}
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Featured boost */}
        {isPublished && stage !== "whelped" && stage !== "converted" && (
          <section id="featured-section" className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="upcoming-featured-section">
            <h2 className="font-spectral text-xl tracking-tight">Featured boost</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {isFeatured
                ? `Currently featured until ${new Date(doc.featured_until).toLocaleDateString()}.`
                : "Pin to the top of the marketplace for 30 days ($15)."}
            </p>
            {!isFeatured && (
              <Button
                disabled={busy}
                onClick={featureBoost}
                className="mt-3 bg-gold-gradient text-ink hover:opacity-90 rounded-full"
                data-testid="upcoming-feature-btn"
              >
                Boost — $15
              </Button>
            )}
          </section>
        )}

        {/* Contract */}
        {isPublished && (
          <section id="contract-section" className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="upcoming-contract-section">
            <h2 className="font-spectral text-xl tracking-tight">Sales contract</h2>
            <p className="text-[12px] text-[#6B6B6B] mt-1">
              Upload your own PDF (reviewed by your lawyer). Armstrong does not
              draft or review contracts.
            </p>
            {contract?.pdf_url && (
              <p className="text-sm mt-2">
                Current: <a className="underline" href={contract.pdf_url} target="_blank" rel="noreferrer">view PDF</a>
                {contract.breeder_signed && " · breeder-signed"}
              </p>
            )}
            {!contract?.breeder_signed && (
              <div className="mt-3 space-y-3">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => uploadContract(e.target.files?.[0])}
                  data-testid="upcoming-contract-upload"
                />
                {contract?.pdf_url && (
                  <>
                    <Input
                      value={legalName}
                      onChange={(e) => setLegalName(e.target.value)}
                      placeholder="Your full legal name"
                      data-testid="upcoming-contract-sign-legalname"
                    />
                    <label className="flex items-start gap-2 text-[12px] text-[#3F3F3F]">
                      <input
                        type="checkbox"
                        checked={ack}
                        onChange={(e) => setAck(e.target.checked)}
                        data-testid="upcoming-contract-sign-ack"
                      />
                      <span>
                        I have reviewed this contract with my own counsel.
                        Armstrong K9 Registry is a venue, not a party to this contract.
                      </span>
                    </label>
                    <Button
                      onClick={signContract}
                      disabled={busy}
                      className="bg-ink hover:bg-crimson text-white rounded-full"
                      data-testid="upcoming-contract-sign-btn"
                    >
                      Sign & publish contract
                    </Button>
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {/* Deposits / waitlist */}
        {isPublished && (
          <section id="waitlist-section" className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="upcoming-waitlist-section">
            <div className="flex items-center justify-between">
              <h2 className="font-spectral text-xl tracking-tight">Waitlist</h2>
              <span className="text-[12px] text-[#6B6B6B]">{deposits.length} paid</span>
            </div>
            {deposits.length === 0 && (
              <p className="text-sm text-[#6B6B6B] mt-2">
                No deposits yet. Share the public listing link to start collecting.
              </p>
            )}
            {deposits.length > 0 && (
              <ul className="mt-3 divide-y divide-[#F2F2F2]">
                {deposits.map((d) => (
                  <li key={d.id} className="py-2 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-bold">
                        #{d.waitlist_rank} · {d.buyer_legal_name || d.buyer_email}
                      </div>
                      <div className="text-[12px] text-[#6B6B6B]">
                        {d.buyer_email}
                        {d.status === "refund_requested" && (
                          <span className="ml-2 text-crimson font-bold">refund requested</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{DOLLARS(d.amount_cents)}</div>
                      <div className="text-[11px] text-[#6B6B6B]">
                        fee: {DOLLARS(d.application_fee_cents)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Whelp action */}
        {isPublished && (stage === "planned" || stage === "expecting") && (
          <section id="whelp-section" className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="upcoming-whelp-section">
            <h2 className="font-spectral text-xl tracking-tight">Mark as whelped</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Once the puppies are born, mark whelped. We'll create a real litter
              row in My Litters, anchor the 8-week go-home rule, spawn one puppy
              placeholder per estimated puppy, and let you assign each buyer.
            </p>
            <div className="mt-3 flex gap-2 items-end">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                  Actual whelp date
                </Label>
                <DateOfBirthInput
                  value={whelpDate}
                  onChange={setWhelpDate}
                  autoComplete="off"
                  ariaLabel="Whelp date"
                  testId="upcoming-whelp-actual-date"
                />
              </div>
              <Button
                onClick={markWhelped}
                disabled={busy}
                className="bg-ink hover:bg-crimson text-white rounded-full"
                data-testid="upcoming-whelp-btn"
              >
                Mark whelped
              </Button>
            </div>
          </section>
        )}

        {/* Puppy assignment (post-whelp) */}
        {stage === "whelped" && puppies.length > 0 && (
          <section id="puppies-section" className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="upcoming-puppies-section">
            <h2 className="font-spectral text-xl tracking-tight">
              Assign puppies to waitlist buyers
            </h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Pick which paid waitlist buyer gets which puppy. Each puppy has a
              real dog record — name it, set the sex, add a photo. Buyers see
              their assigned puppy on the public listing and can sign the
              contract once linked.
            </p>
            <div className="mt-3 space-y-3">
              {puppies.map((p) => (
                <PuppyRow
                  key={p.id}
                  puppy={p}
                  deposits={deposits.filter((d) => d.status === "paid")}
                  onSave={(patch) => updatePuppy(p.id, patch)}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}

function PuppyRow({ puppy, deposits, onSave }) {
  const { id: ubId } = useParams();
  const [name, setName] = useState(puppy.name || "");
  const [sex, setSex] = useState(puppy.sex || "");
  const [photoUrl, setPhotoUrl] = useState(puppy.photo_url || "");
  const [uploading, setUploading] = useState(false);
  const [depositId, setDepositId] = useState(
    puppy.assigned_deposit?.id || ""
  );

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/upcoming-breedings/${ubId}/puppies/${puppy.id}/photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPhotoUrl(r.data.photo_url);
      toast.success("Photo uploaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="border border-[#EAEAEA] rounded-lg p-3 grid sm:grid-cols-[100px_1fr_1fr_1fr_120px] gap-3 items-center"
      data-testid={`puppy-row-${puppy.id}`}
    >
      <label
        className="relative w-[100px] h-[100px] rounded cursor-pointer overflow-hidden"
        data-testid={`puppy-photo-drop-${puppy.id}`}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-[100px] h-[100px] object-cover" />
        ) : (
          <div className="w-[100px] h-[100px] bg-[#FAFAFA] border border-dashed border-[#D5D5D5] rounded flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-[#9B9B9B] hover:bg-[#F2F2F2]">
            {uploading ? "uploading…" : "+ photo"}
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={(e) => uploadPhoto(e.target.files?.[0])}
          disabled={uploading}
        />
      </label>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Puppy name"
        data-testid={`puppy-name-${puppy.id}`}
      />
      <select
        value={sex}
        onChange={(e) => setSex(e.target.value)}
        className="rounded-md border border-[#E5E5E5] p-2 text-sm"
        data-testid={`puppy-sex-${puppy.id}`}
      >
        <option value="">Sex…</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
      </select>
      <select
        value={depositId}
        onChange={(e) => setDepositId(e.target.value)}
        className="rounded-md border border-[#E5E5E5] p-2 text-sm"
        data-testid={`puppy-deposit-${puppy.id}`}
      >
        <option value="">— unassigned —</option>
        {deposits.map((d) => (
          <option key={d.id} value={d.id}>
            #{d.waitlist_rank} · {d.buyer_legal_name || d.buyer_email}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        onClick={() =>
          onSave({
            name,
            sex,
            photo_url: photoUrl || null,
            deposit_id: depositId || null,
          })
        }
        className="bg-ink hover:bg-crimson text-white rounded-full"
        data-testid={`puppy-save-${puppy.id}`}
      >
        Save
      </Button>
    </div>
  );
}

function AnalyticsStrip({ a }) {
  const d = a.deposits || {};
  const p = a.puppies || {};
  const grossDollars = ((d.paid_gross_cents || 0) / 100).toFixed(0);
  const netDollars = ((d.paid_net_to_breeder_cents || 0) / 100).toFixed(0);
  const feeDollars = ((d.paid_application_fee_cents || 0) / 100).toFixed(0);
  const { id: ubId } = useParams();
  // OG-rich share URL (backend serves HTML with og:image + 0s redirect
  // to the SPA). Social scrapers will pick this up and show the
  // 1200×630 share-card; humans get bounced to the real listing.
  const publicUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/upcoming-breedings/${ubId}/share`;
  const shareCardUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/upcoming-breedings/${ubId}/share-card.png`;
  const conv = a.deposit_conversion_pct;
  const views = a.views || 0;

  // Triggered from the "share" CTA cards. Copies the public URL to
  // the clipboard AND opens the auto-generated 1200×630 PNG share
  // card in a new tab so the breeder can right-click → Save to post
  // straight to Instagram / Facebook / X.
  const copyAndShare = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(
        "Public link copied + share image opening in a new tab — right-click to save."
      );
      window.open(shareCardUrl, "_blank", "noopener");
    } catch {
      // Clipboard write failed (likely Safari w/o user gesture quirk);
      // still open the share image so the breeder gets value.
      window.open(shareCardUrl, "_blank", "noopener");
      toast.error("Couldn't auto-copy. Share image opened in a new tab.");
    }
  };

  // Context-aware coach: each card's CTA copy + scroll target depends
  // on where the breeder is in the funnel for that metric. Goal is
  // "you don't have to interpret the numbers — the card tells you
  // exactly what to do next."
  const viewsState = (() => {
    if (views === 0) {
      return {
        action: "Get share card + copy link",
        onClick: copyAndShare,
      };
    }
    if (!a.featured_active && (conv === null || conv === undefined || conv < 5)) {
      return { action: "Boost with $15 — try Featured", onClick: () => goto("featured-section") };
    }
    return {
      action: a.featured_active ? "Manage featured boost" : "Boost with $15 Featured",
      onClick: () => goto("featured-section"),
    };
  })();
  const paidState = (() => {
    if ((d.paid || 0) === 0) {
      return {
        action: "Get share card + copy link",
        onClick: copyAndShare,
      };
    }
    if ((d.refund_requested || 0) > 0) {
      return { action: "Handle refund requests", onClick: () => goto("waitlist-section") };
    }
    return { action: "Manage waitlist", onClick: () => goto("waitlist-section") };
  })();
  const sigState = (() => {
    if (!a.contract_signatures && (d.paid || 0) > 0) {
      return { action: "Publish contract for buyers to sign", onClick: () => goto("contract-section") };
    }
    if ((d.paid || 0) > 0 && a.contract_signatures < d.paid) {
      return { action: "Nudge unsigned buyers", onClick: () => goto("contract-section") };
    }
    return { action: "Manage contract", onClick: () => goto("contract-section") };
  })();
  const puppyState = (() => {
    if (!p.total) return { action: "Mark whelped to spawn puppies", onClick: () => goto("whelp-section") };
    if (p.assigned < p.total) return { action: "Assign next puppy", onClick: () => goto("puppies-section") };
    return { action: "All assigned — review", onClick: () => goto("puppies-section") };
  })();

  const goto = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-[#C9A227]", "ring-offset-2");
      setTimeout(
        () => el.classList.remove("ring-2", "ring-[#C9A227]", "ring-offset-2"),
        1600,
      );
    }
  };
  return (
    <section
      className="rounded-xl border-2 border-[#C9A227] bg-[#FBF7EC] p-5"
      data-testid="upcoming-analytics-strip"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A6A00]">
          Listing performance · click any card to act
        </p>
        <div className="text-[11px] text-ink/55">
          {a.featured_active ? (
            <span className="font-bold text-[#8A6A00]">
              ⭐ Featured until {new Date(a.featured_until).toLocaleDateString()}
            </span>
          ) : (
            "Boost with $15 Featured to surface at the top of the marketplace"
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-5 gap-3">
        <Stat
          label="Listing views"
          v={views}
          sub={
            conv !== null && conv !== undefined
              ? `${conv}% deposit conversion`
              : "need 2+ views"
          }
          action={viewsState.action}
          onClick={viewsState.onClick}
          testId="analytics-views"
        />
        <Stat
          label="Paid deposits"
          v={d.paid || 0}
          sub={`${d.total || 0} total · ${d.refund_requested || 0} refund req`}
          action={paidState.action}
          onClick={paidState.onClick}
          testId="analytics-paid-deposits"
        />
        <Stat
          label="Gross deposits"
          v={`$${grossDollars}`}
          sub={`fee $${feeDollars} · net $${netDollars}`}
          action="See waitlist payouts"
          onClick={() => goto("waitlist-section")}
          testId="analytics-gross"
        />
        <Stat
          label="Contracts signed"
          v={a.contract_signatures || 0}
          sub={
            d.paid
              ? `${Math.round(((a.contract_signatures || 0) / d.paid) * 100)}% of buyers`
              : "—"
          }
          action={sigState.action}
          onClick={sigState.onClick}
          testId="analytics-signatures"
        />
        <Stat
          label="Puppies assigned"
          v={p.total ? `${p.assigned || 0} / ${p.total}` : "—"}
          sub={p.total ? "post-whelp placements" : "not whelped yet"}
          action={puppyState.action}
          onClick={puppyState.onClick}
          testId="analytics-puppies"
        />
      </div>
    </section>
  );
}

function Stat({ label, v, sub, action, onClick, testId }) {
  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={testId}
      className={
        "bg-white border border-[#E5D08A] rounded-lg p-3 text-left transition " +
        (clickable
          ? "hover:border-[#C9A227] hover:shadow-md hover:-translate-y-0.5 cursor-pointer w-full"
          : "")
      }
    >
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00]">
        {label}
      </div>
      <div className="text-2xl font-spectral tracking-tight mt-1 text-ink">
        {v}
      </div>
      {sub && (
        <div className="text-[10.5px] text-ink/55 mt-0.5 leading-tight">{sub}</div>
      )}
      {action && clickable && (
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#8A6A00] mt-2 flex items-center gap-1">
          {action} <span aria-hidden>→</span>
        </div>
      )}
    </Tag>
  );
}

